import React from 'react';

type ModalType = 'info' | 'success' | 'error' | 'warning' | 'confirm';

interface ModalProps {
  isOpen: boolean;
  title: string;
  message?: string;
  type?: ModalType;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

const ICON: Record<ModalType, string> = {
  info: 'info', success: 'check_circle', error: 'error_outline',
  warning: 'warning_amber', confirm: 'help_outline',
};
const COLOR: Record<ModalType, string> = {
  info: 'var(--primary)', success: 'var(--success)', error: 'var(--error)',
  warning: 'var(--warning)', confirm: 'var(--warning)',
};

export default function Modal({
  isOpen, title, message, type = 'info',
  confirmText = 'OK', cancelText, onConfirm, onCancel,
}: ModalProps) {
  if (!isOpen) return null;
  const hasCancel = !!cancelText && !!onCancel;
  const confirmClass = (type === 'confirm' || type === 'error') && hasCancel ? 'btn-danger' : 'btn-primary';

  return (
    <div className="modal-overlay" onClick={onCancel ?? onConfirm}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-icon-wrap" style={{ color: COLOR[type] }}>
          <span className="mi" style={{ fontSize: 40 }}>{ICON[type]}</span>
        </div>
        <div className="modal-title">{title}</div>
        {message && <div className="modal-msg">{message}</div>}
        <div className={`modal-actions${hasCancel ? ' two' : ''}`}>
          {hasCancel && (
            <button className="btn btn-cancel" onClick={onCancel}>{cancelText}</button>
          )}
          <button className={`btn ${confirmClass}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export type ModalConfig = {
  title: string; message?: string; type?: ModalType;
  confirmText?: string; cancelText?: string;
  onConfirm: () => void; onCancel?: () => void;
};

export function useModal() {
  const [modal, setModal] = React.useState<ModalConfig | null>(null);
  const show = React.useCallback((cfg: ModalConfig) => setModal(cfg), []);
  const close = React.useCallback(() => setModal(null), []);
  return { modal, show, close };
}
